import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api from '../../../services/api';

const Step12FinalSubmit = () => {
    const { mockUser, refreshUser } = useOutletContext();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    
    // Checklist State
    const [checklist, setChecklist] = useState({
        name: false,
        fatherName: false,
        motherName: false,
        dob: false,
        gender: false,
        category: false,
        personWithDisability: false,
        educationalDetails: false,
        examCity: false
    });

    const [finalDeclaration, setFinalDeclaration] = useState(false);

    // Load All Data
    useEffect(() => {
        const init = async () => {
            try {
                const res = await api.get(`/mock-cuet/application-data?mockAppId=${mockUser.id}`);
                if (res.data.success) {
                    setData(res.data.data);
                }
            } catch (err) { console.error(err); }
        };
        init();
    }, [mockUser]);

    // Handlers
    const handleChecklistChange = (e) => {
        setChecklist({ ...checklist, [e.target.name]: e.target.checked });
    };

    const isSubmitEnabled = () => {
        // All checklist items must be true AND final declaration must be true
        const allChecked = Object.values(checklist).every(v => v === true);
        return allChecked && finalDeclaration;
    };

    const handleSubmit = async () => {
        if (!isSubmitEnabled()) return;

        if (!window.confirm("Are you sure you want to finally submit the application? No changes will be allowed after this.")) {
            return;
        }

        setLoading(true);

        try {
            const res = await api.post('/mock-cuet/save-step', {
                mockAppId: mockUser.id,
                stepNumber: 12,
                stepData: {}, // No data to save, just status update
                nextStepNumber: 13
            });

            if (res.data.success) {
                refreshUser(13);
                alert("Application Submitted Successfully!");
                // You could redirect to a print page or home
                navigate('/services/counselling/dashboard'); 
            }
        } catch (err) {
            alert("Error submitting application");
        } finally {
            setLoading(false);
        }
    };

    if (!data) return <div className="text-center p-5">Loading Review Data...</div>;

    // Helper to render a Review Row
    const ReviewRow = ({ label, value }) => (
        <tr>
            <td style={{width:'30%', fontWeight:'bold', background:'#f9f9f9'}}>{label}</td>
            <td style={{width:'70%'}}>{value || '-'}</td>
        </tr>
    );

    return (
        <div id="ctl00_ContentPlaceHolder1_pnlApplicationForm">
            <div className="appstatus" style={{ paddingTop: '0px', marginTop: '0px' }}>
                
                <h4 className="text-center text-primary" style={{fontWeight:'bold', borderBottom:'2px solid #0094c3', paddingBottom:'10px'}}>
                    Review Page - Online Application Form
                </h4>

                <div className="alert alert-warning text-center">
                    <strong>Please review the following information carefully. If you would like to change any information, please click the 'Back' button or select the specific section from the left menu.</strong>
                </div>

                {/* 1. Personal Details */}
                <div className="panel panel-info">
                    <div className="panel-heading">Personal Details</div>
                    <div className="panel-body" style={{padding:0}}>
                        <table className="table table-bordered" style={{marginBottom:0}}>
                            <tbody>
                                <ReviewRow label="Candidate's Name" value={data.candidate_name} />
                                <ReviewRow label="Father's Name" value={data.father_name} />
                                <ReviewRow label="Mother's Name" value={data.mother_name} />
                                <ReviewRow label="Date of Birth" value={data.dob ? new Date(data.dob).toLocaleDateString('en-GB') : '-'} />
                                <ReviewRow label="Gender" value={data.gender === '01' ? 'Male' : data.gender === '02' ? 'Female' : 'Third Gender'} />
                                <ReviewRow label="Nationality" value={data.nationality === '01' ? 'Indian' : 'Other'} />
                                <ReviewRow label="Category" value={data.category === '01' ? 'General' : data.category === '44' ? 'OBC-NCL' : data.category} />
                                <ReviewRow label="PwD Candidate" value={data.pwbd_candidate === 1 ? 'Yes' : 'No'} />
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 2. Contact Details */}
                <div className="panel panel-info">
                    <div className="panel-heading">Contact Details</div>
                    <div className="panel-body" style={{padding:0}}>
                        <table className="table table-bordered" style={{marginBottom:0}}>
                            <tbody>
                                <ReviewRow label="Present Address" value={`${data.pres_address}, ${data.pres_locality}, ${data.pres_district}, ${data.pres_state} - ${data.pres_pincode}`} />
                                <ReviewRow label="Email ID" value={data.email} />
                                <ReviewRow label="Mobile Number" value={data.mobile} />
                                <ReviewRow label="Permanent Address" value={data.same_as_present ? "Same as Present Address" : `${data.perm_address}, ...`} />
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 3. Identity Details */}
                <div className="panel panel-info">
                    <div className="panel-heading">Identity Details</div>
                    <div className="panel-body" style={{padding:0}}>
                        <table className="table table-bordered" style={{marginBottom:0}}>
                            <tbody>
                                <ReviewRow label="Identity Type" value={data.identity_type} />
                                <ReviewRow label="Identity Number" value={data.identity_number} />
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 4. Exam Center & Subjects */}
                <div className="panel panel-info">
                    <div className="panel-heading">Test Paper / Subject Selection & Exam Center</div>
                    <div className="panel-body" style={{padding:0}}>
                        <table className="table table-bordered" style={{marginBottom:0}}>
                            <tbody>
                                <ReviewRow label="Question Paper Medium" value={data.question_paper_medium === '01' ? 'English' : data.question_paper_medium === '02' ? 'Hindi' : data.question_paper_medium} />
                                <ReviewRow 
                                    label="Selected Subjects" 
                                    value={data.selected_subjects ? JSON.parse(data.selected_subjects).join(', ') : '-'} 
                                />
                                <ReviewRow label="Exam City Preference 1" value={data.exam_center_1_city} />
                                <ReviewRow label="Exam City Preference 2" value={data.exam_center_2_city} />
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 5. Documents Uploaded Images */}
                <div className="panel panel-info">
                    <div className="panel-heading">Uploaded Images</div>
                    <div className="panel-body">
                        <div className="row text-center">
                            <div className="col-sm-3">
                                <strong>Photograph</strong><br/>
                                {data.doc_passport_photo ? <img src={data.doc_passport_photo} alt="Photo" style={{height:'100px', marginTop:'5px'}} /> : 'Not Uploaded'}
                            </div>
                            <div className="col-sm-3">
                                <strong>Signature</strong><br/>
                                {data.doc_signature ? <img src={data.doc_signature} alt="Sig" style={{height:'50px', marginTop:'25px'}} /> : 'Not Uploaded'}
                            </div>
                            <div className="col-sm-3">
                                <strong>Live Capture</strong><br/>
                                {data.captured_photo ? <img src={data.captured_photo} alt="Live" style={{height:'100px', marginTop:'5px'}} /> : 'Not Uploaded'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- CHECKLIST --- */}
                <div className="panel panel-default">
                    <div className="panel-heading" style={{background:'#fcf8e3', color:'#8a6d3b'}}>
                        <strong>Particulars Checklist to be Verified</strong>
                        <p style={{fontSize:'12px'}}>Kindly verify all the particulars listed below carefully. Once submitted, Application Form cannot be changed.</p>
                    </div>
                    <div className="panel-body">
                        <div className="row">
                            {Object.keys(checklist).map((key) => (
                                <div className="col-sm-4" key={key}>
                                    <div className="checkbox">
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                name={key} 
                                                checked={checklist[key]} 
                                                onChange={handleChecklistChange} 
                                            /> {key.replace(/([A-Z])/g, ' $1').trim()} {/* Adds space before caps */}
                                        </label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* --- DECLARATION --- */}
                <div className="panel panel-default">
                    <div className="panel-heading"><strong>Declaration</strong></div>
                    <div className="panel-body">
                        <div className="checkbox">
                            <label>
                                <input 
                                    type="checkbox" 
                                    checked={finalDeclaration} 
                                    onChange={(e) => setFinalDeclaration(e.target.checked)} 
                                /> 
                                <strong>I hereby declare that the information submitted above is true to the best of my knowledge...</strong> (Full declaration text)
                            </label>
                        </div>
                    </div>
                </div>

                {/* --- BUTTONS --- */}
                <div className="row text-center" style={{marginBottom:'30px'}}>
                    <button 
                        className="btn btn-warning btn-lg" 
                        onClick={() => navigate('/services/counselling/dashboard/upload-documents')} 
                        style={{marginRight:'20px'}}
                    >
                        Back
                    </button>
                    <button 
                        className="btn btn-success btn-lg" 
                        onClick={handleSubmit} 
                        disabled={!isSubmitEnabled() || loading}
                    >
                        {loading ? 'Submitting...' : 'Final Submit'}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default Step12FinalSubmit;